import { useState, useRef, useEffect, useCallback } from "react";
import { Play, Pause, Volume2, VolumeX, WifiOff, Download, Loader2 } from "lucide-react";
import { clearResume } from "@/lib/resumePlayback";
import { isOfflineDownloadOrphaned, downloadEpisode, getOfflineAudioUrl } from "@/lib/offlineEpisodes";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { playerStore, usePlayer, buildEpisodeKey } from "@/lib/playerStore";
import { upNextStore } from "@/lib/upNextStore";
import { toast } from "sonner";

interface AudioPlayerProps {
  src: string;
  title?: string;
  podcastName?: string;
  artworkUrl?: string;
  /** Episode UUID — enables cross-device resume via the database */
  episodeId?: string | null;
  /** Slug used by the persistent mini-bar to deep-link back here. */
  episodeSlug?: string | null;
  /** Start playback at this time (seconds) */
  startTime?: number;
  /** Increment to trigger a re-seek to startTime and play */
  seekSignal?: number;
  /** Begin playback automatically once metadata is loaded */
  autoPlay?: boolean;
  /** Optional playlist context — pinned in Up Next so the rest plays next. */
  playlistId?: string | null;
  /** Episode duration from the API, used to render the bar before the global player loads metadata. */
  durationSeconds?: number;
}

export default function AudioPlayer({
  src,
  title,
  podcastName,
  artworkUrl,
  episodeId,
  episodeSlug,
  startTime,
  seekSignal,
  autoPlay,
  playlistId,
  durationSeconds,
}: AudioPlayerProps) {
  const trackKey = buildEpisodeKey(episodeId, src);
  const progressRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Global player state — only "ours" when our key matches.
  const currentKey = usePlayer((s) => s.current?.key || null);
  const storePlaying = usePlayer((s) => s.playing);
  const storeTime = usePlayer((s) => s.currentTime);
  const storeDuration = usePlayer((s) => s.duration);
  const storeMuted = usePlayer((s) => s.muted);
  const storeBuffering = usePlayer((s) => s.buffering);
  const isActive = currentKey === trackKey;
  const playing = isActive ? storePlaying : false;
  const currentTime = isActive ? storeTime : 0;
  const duration = isActive ? storeDuration : 0;
  const muted = isActive ? storeMuted : false;

  // Use the API-provided duration as a fallback so the bar, the total time,
  // and the magic-seek target are all visible the moment the card loads.
  const effectiveDuration = Math.max(duration, durationSeconds || 0);

  // UX state that stays local to this card.
  const [overrideStart, setOverrideStart] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [magicSeekPlayed, setMagicSeekPlayed] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  // Offline / orphan detection (still surfaced here for the per-episode card UI).
  const [usingLocalBlob, setUsingLocalBlob] = useState(false);
  const [orphaned, setOrphaned] = useState(false);
  const [reDownloading, setReDownloading] = useState(false);
  const [reDownloadProgress, setReDownloadProgress] = useState(0);
  const online = useOnlineStatus();

  const fmt = (s: number) => {
    if (!s || !isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };
  const fmtShort = fmt;

  const loadIntoStore = useCallback((opts?: { startAt?: number | null }) => {
    // Tell the Up Next queue this is now the active playlist context so the
    // remaining playlist items get queued first.
    upNextStore.setCurrentPlaylist(playlistId ?? null);
    playerStore.playEpisode({
      key: trackKey,
      episodeId: episodeId || null,
      src,
      title: title || "Episode",
      podcastName,
      artworkUrl,
      episodeSlug: episodeSlug || null,
      startTime: opts?.startAt ?? null,
    });
  }, [trackKey, episodeId, src, title, podcastName, artworkUrl, episodeSlug, playlistId]);

  const toggle = useCallback(() => {
    setHasInteracted(true);
    if (!isActive) {
      const startAt = !overrideStart && startTime ? startTime : null;
      loadIntoStore({ startAt });
      return;
    }
    playerStore.toggle();
  }, [isActive, loadIntoStore, overrideStart, startTime]);

  // Check offline status (for showing the "downloaded / orphaned" hints).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!episodeId) {
        setUsingLocalBlob(false);
        setOrphaned(false);
        return;
      }
      const local = await getOfflineAudioUrl(episodeId);
      if (cancelled) {
        if (local) URL.revokeObjectURL(local);
        return;
      }
      if (local) {
        URL.revokeObjectURL(local);
        setUsingLocalBlob(true);
        setOrphaned(false);
      } else {
        setUsingLocalBlob(false);
        const isOrphan = await isOfflineDownloadOrphaned(episodeId);
        if (!cancelled) setOrphaned(isOrphan);
      }
    })();
    return () => { cancelled = true; };
  }, [episodeId]);

  // Autoplay handoff: if asked, route this episode into the global player on mount.
  useEffect(() => {
    if (!autoPlay) return;
    if (isActive) return;
    const startAt = !overrideStart && startTime ? startTime : null;
    loadIntoStore({ startAt });
    setHasInteracted(true);
  }, [autoPlay]); // eslint-disable-line react-hooks/exhaustive-deps

  // Magic-seek replay when a new seekSignal arrives (e.g. "jump to relevant moment")
  useEffect(() => {
    if (seekSignal === undefined || seekSignal === 0) return;
    if (startTime == null) return;
    setHasInteracted(true);
    setOverrideStart(false);
    setMagicSeekPlayed(false);
    requestAnimationFrame(() => setMagicSeekPlayed(true));
    if (!isActive) {
      loadIntoStore({ startAt: startTime });
    } else {
      playerStore.seek(startTime);
    }
  }, [seekSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch for the player scrolling into view — only then trigger the magic-seek animation
  useEffect(() => {
    const el = containerRef.current;
    if (!el || hasBeenVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setHasBeenVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
            setHasBeenVisible(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold: [0, 0.4, 1] }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasBeenVisible]);

  useEffect(() => {
    if (magicSeekPlayed) return;
    if (!hasBeenVisible) return;
    if (startTime == null || startTime <= 0 || !effectiveDuration) return;
    const t = setTimeout(() => setMagicSeekPlayed(true), 3000);
    return () => clearTimeout(t);
  }, [effectiveDuration, startTime, magicSeekPlayed, hasBeenVisible]);


  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const bar = progressRef.current;
    if (!bar) return;
    setHasInteracted(true);
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    if (!isActive) {
      loadIntoStore({ startAt: null });
      // Defer the seek until after the track loads.
      requestAnimationFrame(() => {
        const d = playerStore.getState().duration || effectiveDuration;
        if (d) playerStore.seek(pct * d);
      });
      return;
    }
    if (effectiveDuration > 0) playerStore.seek(pct * effectiveDuration);
  };

  const pct = effectiveDuration > 0 ? (currentTime / effectiveDuration) * 100 : 0;
  const showJumpCaption = startTime != null && startTime > 30;

  const targetPct =
    startTime != null && effectiveDuration > 0
      ? Math.min(100, (startTime / effectiveDuration) * 100)
      : 0;

  const showOfflineFallback = !usingLocalBlob && (orphaned || !online);

  const handleReDownload = async () => {
    if (!episodeId || !src) return;
    if (!online) {
      toast.error("You're offline. Reconnect to re-download this episode.");
      return;
    }
    setReDownloading(true);
    setReDownloadProgress(0);
    try {
      await downloadEpisode(
        {
          episodeId,
          audioUrl: src,
          title: title || "Episode",
          podcastName: podcastName || "",
          artworkUrl: artworkUrl,
        },
        setReDownloadProgress,
      );
      toast.success("Episode re-downloaded for offline use");
      setUsingLocalBlob(true);
      setOrphaned(false);
    } catch (err) {
      console.error("Re-download failed:", err);
      toast.error(err instanceof Error ? err.message : "Re-download failed");
    } finally {
      setReDownloading(false);
      setReDownloadProgress(0);
    }
  };

  return (
    <div ref={containerRef} className="bg-muted/50 border-t-2 border-border">
      {showOfflineFallback && (
        <div className="px-3 py-3 border-b-2 border-border bg-background/60">
          <div className="flex items-start gap-3">
            <WifiOff className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-retro font-bold text-sm leading-tight">
                {orphaned
                  ? "This download was cleared by your browser"
                  : "You're offline"}
              </p>
              <p className="font-mono text-[11px] sm:text-xs text-muted-foreground leading-relaxed mt-0.5">
                {orphaned
                  ? "The audio was evicted to free up space. Re-download to listen offline."
                  : "This episode isn't saved on your device. Connect to the internet or download it for offline listening."}
              </p>
              {episodeId && src && (
                <button
                  type="button"
                  onClick={handleReDownload}
                  disabled={reDownloading || !online}
                  title={
                    !online
                      ? "Reconnect to the internet to re-download"
                      : reDownloading
                      ? "Re-downloading…"
                      : "Re-download this episode for offline playback"
                  }
                  className="mt-2 inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-mono font-bold px-2.5 py-1 border-2 border-foreground rounded-md bg-[hsl(195,85%,90%)] text-[hsl(195,80%,20%)] hover:bg-[hsl(195,85%,80%)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {reDownloading ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span className="tabular-nums">{reDownloadProgress}%</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-3 h-3" />
                      <span>{online ? "Re-download for offline" : "Waiting for connection"}</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showJumpCaption && (
        <div className="px-3 pt-2 pb-1 flex items-center justify-between gap-2 text-[10px] sm:text-xs font-mono">
          <span className="text-muted-foreground italic truncate">
            {overrideStart ? (
              <>▶ Will play from the beginning</>
            ) : (
              <>✨ Cued to <span className="font-bold text-accent">{fmtShort(startTime!)}</span> — the most relevant moment</>
            )}
          </span>
          <button
            type="button"
            onClick={() => {
              setHasInteracted(true);
              if (overrideStart) {
                setOverrideStart(false);
                if (!isActive) loadIntoStore({ startAt: startTime! });
                else playerStore.seek(startTime!);
              } else {
                setOverrideStart(true);
                clearResume(src, episodeId);
                if (!isActive) loadIntoStore({ startAt: 0 });
                else playerStore.seek(0);
              }
            }}
            className="flex-shrink-0 underline text-accent hover:text-foreground transition-colors whitespace-nowrap"
          >
            {overrideStart ? `Jump to ${fmtShort(startTime!)}` : "Play from beginning"}
          </button>
        </div>
      )}

      <div className="flex items-center gap-2 sm:gap-3 px-3 py-2.5">
        {/* Play/Pause — comic book style */}
        <button
          onClick={toggle}
          className="comic-play-btn flex-shrink-0 w-11 h-11 sm:w-10 sm:h-10 border-[3px] border-[hsl(195,80%,28%)] rounded-md bg-[hsl(195,85%,55%)] text-white flex items-center justify-center transition-all duration-150 transform rotate-[-2deg] hover:rotate-0 hover:scale-110 active:scale-90 shadow-[4px_4px_0px_0px_hsl(var(--foreground))] hover:shadow-[2px_2px_0px_0px_hsl(var(--foreground))] hover:translate-x-[2px] hover:translate-y-[2px] relative overflow-hidden"
          aria-label={playing ? "Pause" : "Play"}
          title={playing ? "Pause this episode" : "Play this episode"}
        >
          {playing ? (
            <Pause className="w-5 h-5 relative z-10" strokeWidth={3} />
          ) : (
            <Play className="w-5 h-5 ml-0.5 relative z-10" strokeWidth={3} fill="currentColor" />
          )}
        </button>

        {/* Time */}
        <span className="font-mono text-[10px] sm:text-xs text-muted-foreground tabular-nums flex-shrink-0 w-8 sm:w-10 text-right">
          {fmt(currentTime)}
        </span>

        {/* Progress bar */}
        {(() => {
          const magicBase = !overrideStart && targetPct > 0;
          const showGhost = magicBase;
          const ghostWidth = showGhost ? (magicSeekPlayed || hasInteracted ? targetPct : 0) : 0;
          const showSparkle = magicBase && (magicSeekPlayed || hasInteracted);
          const isInitializing = isActive && (storeBuffering || duration === 0);
          return (
            <div
              ref={progressRef}
              onClick={seek}
              className="flex-1 relative group py-3 -my-3 cursor-pointer"
              style={{ touchAction: "manipulation" }}
              aria-busy={isInitializing || undefined}
            >
              <div className="h-2 sm:h-2.5 bg-foreground/10 relative overflow-hidden">
                {showGhost && (
                  <div
                    className="absolute inset-y-0 left-0 bg-accent/25 pointer-events-none"
                    style={{ width: `${ghostWidth}%`, transition: "width 3000ms cubic-bezier(0.22, 1, 0.36, 1)" }}
                  />
                )}
                <div
                  className="absolute inset-y-0 left-0 bg-accent pointer-events-none z-10"
                  style={{ width: `${pct}%`, transition: "width 100ms linear" }}
                />
                {isInitializing && (
                  <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none z-20 animate-audio-skeleton"
                    style={{
                      backgroundImage:
                        "linear-gradient(90deg, transparent 0%, hsl(var(--accent) / 0.35) 50%, transparent 100%)",
                      backgroundSize: "40% 100%",
                      backgroundRepeat: "no-repeat",
                    }}
                  />
                )}
                {showSparkle && (
                  <span
                    aria-hidden
                    className="magic-sparkle"
                    style={{ left: `calc(${targetPct}% - 7px)`, zIndex: 25 }}
                  />
                )}
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-3 h-3 sm:w-3 sm:h-3 bg-foreground border-2 border-accent opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity pointer-events-none z-30"
                  style={{ left: `calc(${pct}% - 6px)` }}
                />
              </div>
              {isInitializing && (
                <span className="sr-only">Loading audio…</span>
              )}
            </div>
          );
        })()}

        {/* Duration */}
        <span className="font-mono text-[10px] sm:text-xs text-muted-foreground tabular-nums flex-shrink-0 w-8 sm:w-10">
          {fmt(effectiveDuration)}
        </span>

        {/* Mute */}
        <button
          onClick={() => playerStore.setMuted(!muted)}
          className="flex-shrink-0 p-2.5 sm:p-1.5 -m-1 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={muted ? "Unmute" : "Mute"}
          title={muted ? "Unmute audio" : "Mute audio"}
        >
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
